import { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Snackbar,
  Chip,
  Tooltip,
  Stack,
  Tabs,
  Tab,
  Checkbox,
} from "@mui/material";
import {
  Share as ShareIcon,
  Link as LinkIcon,
  ContentCopy as CopyIcon,
  PersonAdd as PersonAddIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import Layout from "../components/layout/Layout";
import config from "../config";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { setKycData, setSelectedCompanyId, setSelectedCompanyDetails } from "../features/counter/kycSlice";
import { useNavigate } from "react-router-dom";

const KycShare = () => {
  const { API_BASE_URL } = config;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user, token } = useSelector((state) => state.auth);
  const { kycData } = useSelector((state) => state.kyc);

  const [loading, setLoading] = useState(true);
  const [sharedWithMeLoading, setSharedWithMeLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [shareType, setShareType] = useState("link");
  const [targetEmail, setTargetEmail] = useState("");
  const [accessLevel, setAccessLevel] = useState("view");
  const [generatedLink, setGeneratedLink] = useState("");
  const [expiresAt, setExpiresAt] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [submitting, setSubmitting] = useState(false);
  const [sharedWithMeData, setSharedWithMeData] = useState([]);
  const [selectedSharedKycs, setSelectedSharedKycs] = useState({});

  // Fetch user's KYC data (only)
  useEffect(() => {
    const fetchKycData = async () => {
      try {
        setLoading(true);
        const kycResponse = await axios.get(
          `${API_BASE_URL}/api/v1/getKycData/${user.id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        const kycDataArray = Array.isArray(kycResponse.data.kycData)
          ? kycResponse.data.kycData
          : [];

        console.log("KYC Data fetched:", kycDataArray);
        dispatch(setKycData(kycDataArray));
      } catch (err) {
        console.error("Error fetching data:", err);
        showSnackbar("Error fetching KYC data", "error");
      } finally {
        setLoading(false);
      }
    };

    if (token && user?.id) {
      fetchKycData();
    }
  }, [API_BASE_URL, token, user?.id, dispatch]);

  // Fetch KYC shared with me
  useEffect(() => {
    const fetchSharedWithMe = async () => {
      try {
        setSharedWithMeLoading(true);
        const response = await axios.get(
          `${API_BASE_URL}/api/v1/kyc/shared-with-me`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        console.log("Shared with me response:", response.data);

        const sharedData = response.data?.data
          ? Array.isArray(response.data.data)
            ? response.data.data
            : [response.data.data]
          : Array.isArray(response.data)
          ? response.data
          : [];

        console.log("Processed shared data:", sharedData);
        setSharedWithMeData(sharedData);
      } catch (err) {
        console.error("Error fetching shared data:", err);
        if (err.response?.status !== 404) {
          showSnackbar("Error fetching shared KYC data", "error");
        }
      } finally {
        setSharedWithMeLoading(false);
      }
    };

    if (token) {
      fetchSharedWithMe();
    }
  }, [API_BASE_URL, token]);

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleShare = (company) => {
    setSelectedCompany(company);
    setShareDialogOpen(true);
    setGeneratedLink("");
    setExpiresAt(null);
    setTargetEmail("");
    setAccessLevel("view");
  };

  const handleCloseDialog = () => {
    setShareDialogOpen(false);
    setSelectedCompany(null);
    setGeneratedLink("");
    setExpiresAt(null);
    setTargetEmail("");
  };

  const handleGenerateLink = async () => {
    if (!selectedCompany) return;

    try {
      setSubmitting(true);
      const kycId = selectedCompany._id || selectedCompany.id;

      if (!kycId) {
        showSnackbar("Error: KYC ID not found", "error");
        return;
      }

      const response = await axios.post(
        `${API_BASE_URL}/api/v1/kyc/generate-share-link`,
        {
          kycId: kycId,
          accessLevel,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const shareToken =
        response.data?.data?.token ||
        response.data?.token ||
        response.data?.accessToken;

      if (!shareToken) {
        throw new Error("No token received from server");
      }

      const shareUrl = `${window.location.origin}/kyc/view/${shareToken}`;
      setGeneratedLink(shareUrl);

      const expirationDate =
        response.data?.data?.expiresAt || response.data?.expiresAt;
      setExpiresAt(expirationDate);

      const formattedDate = expirationDate
        ? new Date(expirationDate).toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
          })
        : "in 7 days";

      showSnackbar(
        response.data?.message ||
          `Share link generated successfully! Expires ${formattedDate}`,
        "success"
      );
    } catch (error) {
      console.error("Error generating link:", error);
      showSnackbar(
        error.response?.data?.message || error.message || "Failed to generate link",
        "error"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleShareWithUser = async () => {
    if (!selectedCompany || !targetEmail.trim()) {
      showSnackbar("Please enter a valid email", "warning");
      return;
    }

    try {
      setSubmitting(true);
      const kycId = selectedCompany._id || selectedCompany.id;

      if (!kycId) {
        showSnackbar("Error: KYC ID not found", "error");
        return;
      }

      await axios.post(
        `${API_BASE_URL}/api/v1/kyc/share`,
        {
          kycId: kycId,
          targetEmail: targetEmail.trim(),
          accessLevel,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      showSnackbar("KYC shared successfully with user!", "success");
      handleCloseDialog();
    } catch (error) {
      console.error("Error sharing with user:", error);
      showSnackbar(
        error.response?.data?.message || "Failed to share with user",
        "error"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(generatedLink);
    showSnackbar("Link copied to clipboard!", "success");
  };

  const handleSelectSharedKyc = (kycId) => {
    setSelectedSharedKycs((prev) => ({
      ...prev,
      [kycId]: !prev[kycId],
    }));
  };

  // Handle viewing selected KYCs - dispatch to Redux and navigate
  const handleViewSelectedKycs = () => {
    const selected = Object.keys(selectedSharedKycs).filter(
      (id) => selectedSharedKycs[id]
    );

    if (selected.length === 0) {
      showSnackbar("Please select at least one KYC document", "warning");
      return;
    }

    // Get the first selected shared KYC
    const selectedSharedKyc = sharedWithMeData.find(
      (item) => item._id === selected[0]
    );

    if (selectedSharedKyc) {
      // Dispatch KYC ID to Redux
      dispatch(setSelectedCompanyId(selectedSharedKyc.kycId._id));

      // Dispatch company details to Redux
      dispatch(
        setSelectedCompanyDetails({
          companyInfo: selectedSharedKyc.kycId.companyInfo,
          address: selectedSharedKyc.kycId.address,
          contactInfo: selectedSharedKyc.kycId.contactInfo,
          businessInfo: selectedSharedKyc.kycId.businessInfo,
          taxInfo: selectedSharedKyc.kycId.taxInfo,
          complianceInfo: selectedSharedKyc.kycId.complianceInfo,
          bankingInfo: selectedSharedKyc.kycId.bankingInfo,
        })
      );

      console.log("Selected KYCs to view:", selected);
      showSnackbar(`Viewing ${selected.length} selected KYC document(s)`, "info");

      // Navigate to Dashboard
      navigate("/Dashboard");
    }
  };

  const kycDataArray = Array.isArray(kycData) ? kycData : [];

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" sx={{ mb: 3 }}>
          KYC Management
        </Typography>

        {/* Tabs for switching between sections */}
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}
        >
          <Tab label="My KYC Books" />
          <Tab label="Shared With Me" />
        </Tabs>

        {/* Tab 1: My KYC Books */}
        {activeTab === 0 && (
          <Box>
            {loading ? (
              <Box>
                <Skeleton
                  variant="text"
                  width={200}
                  height={30}
                  sx={{ mb: 2 }}
                />
                <Skeleton variant="rectangular" height={200} />
              </Box>
            ) : kycDataArray.length === 0 ? (
              <Box sx={{ textAlign: "center", mt: 4 }}>
                <Typography variant="h6" gutterBottom>
                  No KYC Information Found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Add companies first to share them
                </Typography>
              </Box>
            ) : (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Share Your Companies
                </Typography>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                        <TableCell>Company Name</TableCell>
                        <TableCell>Registration Number</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {kycDataArray.map((company) => (
                        <TableRow
                          key={company._id || company.companyInfo.registrationNumber}
                        >
                          <TableCell>
                            {company.companyInfo.legalName}
                          </TableCell>
                          <TableCell>
                            {company.companyInfo.registrationNumber}
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="Share KYC">
                              <IconButton
                                color="primary"
                                onClick={() => handleShare(company)}
                              >
                                <ShareIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Box>
        )}

        {/* Tab 2: Shared With Me */}
        {activeTab === 1 && (
          <Box>
            {sharedWithMeLoading ? (
              <Box>
                <Skeleton
                  variant="text"
                  width={200}
                  height={30}
                  sx={{ mb: 2 }}
                />
                <Skeleton variant="rectangular" height={200} />
              </Box>
            ) : sharedWithMeData.length === 0 ? (
              <Box sx={{ textAlign: "center", mt: 4 }}>
                <Typography variant="h6" gutterBottom>
                  No KYC Documents Shared With You
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  KYC documents shared with you will appear here
                </Typography>
              </Box>
            ) : (
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography variant="h6">KYC Books Shared With Me</Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleViewSelectedKycs}
                    disabled={
                      Object.values(selectedSharedKycs).every((v) => !v)
                    }
                  >
                    View Selected ({Object.values(selectedSharedKycs).filter((v) => v).length})
                  </Button>
                </Box>

                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                        <TableCell padding="checkbox" width={50}>
                          <Tooltip title="Select all">
                            <Checkbox
                              indeterminate={
                                Object.values(selectedSharedKycs).some(
                                  (v) => v
                                ) &&
                                !Object.values(selectedSharedKycs).every(
                                  (v) => v
                                )
                              }
                              checked={
                                sharedWithMeData.length > 0 &&
                                Object.values(selectedSharedKycs).every(
                                  (v) => v
                                )
                              }
                              onChange={(e) => {
                                const newSelected = {};
                                sharedWithMeData.forEach((item) => {
                                  newSelected[item._id] = e.target.checked;
                                });
                                setSelectedSharedKycs(newSelected);
                              }}
                            />
                          </Tooltip>
                        </TableCell>
                        <TableCell>Company Name</TableCell>
                        <TableCell>Shared By</TableCell>
                        <TableCell>Access Level</TableCell>
                        <TableCell>Shared At</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sharedWithMeData.map((item) => (
                        <TableRow key={item._id}>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={!!selectedSharedKycs[item._id]}
                              onChange={() => handleSelectSharedKyc(item._id)}
                            />
                          </TableCell>
                          <TableCell>
                            {item.kycId?.companyInfo?.legalName ||
                              "N/A"}
                          </TableCell>
                          <TableCell>
                            {item.ownerId?.email ||
                              item.ownerId?.username ||
                              "Unknown"}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={item.accessLevel === "edit" ? "Edit" : "View Only"}
                              color={
                                item.accessLevel === "edit"
                                  ? "warning"
                                  : "default"
                              }
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {new Date(item.createdAt).toLocaleDateString(
                              "en-US",
                              {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                              }
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Box>
        )}

        {/* Share Dialog */}
        <Dialog
          open={shareDialogOpen}
          onClose={handleCloseDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            Share KYC: {selectedCompany?.companyInfo?.legalName}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <Stack spacing={3}>
                {/* Share Type Selection */}
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Share Method
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <Chip
                      icon={<LinkIcon />}
                      label="Generate Link"
                      onClick={() => setShareType("link")}
                      color={shareType === "link" ? "primary" : "default"}
                      variant={shareType === "link" ? "filled" : "outlined"}
                    />
                    <Chip
                      icon={<PersonAddIcon />}
                      label="Share with User"
                      onClick={() => setShareType("user")}
                      color={shareType === "user" ? "primary" : "default"}
                      variant={shareType === "user" ? "filled" : "outlined"}
                    />
                  </Stack>
                </Box>

                {/* Access Level */}
                <FormControl fullWidth>
                  <InputLabel>Access Level</InputLabel>
                  <Select
                    value={accessLevel}
                    label="Access Level"
                    onChange={(e) => setAccessLevel(e.target.value)}
                  >
                    <MenuItem value="view">View Only</MenuItem>
                    <MenuItem value="edit">View & Edit</MenuItem>
                  </Select>
                </FormControl>

                {/* Share with User */}
                {shareType === "user" && (
                  <TextField
                    fullWidth
                    label="User Email"
                    type="email"
                    value={targetEmail}
                    onChange={(e) => setTargetEmail(e.target.value)}
                    placeholder="Enter user's email address"
                    helperText="Enter the email of the user you want to share with"
                  />
                )}

                {/* Generated Link */}
                {shareType === "link" && generatedLink && (
                  <Box>
                    <Alert severity="success" sx={{ mb: 2 }}>
                      Share link generated successfully!
                    </Alert>
                    <TextField
                      fullWidth
                      label="Share Link"
                      value={generatedLink}
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <IconButton onClick={handleCopyLink} edge="end">
                            <CopyIcon />
                          </IconButton>
                        ),
                      }}
                    />
                    {expiresAt && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 1, display: "block" }}
                      >
                        Expires on:{" "}
                        {new Date(expiresAt).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })}
                      </Typography>
                    )}
                  </Box>
                )}
              </Stack>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            {shareType === "link" ? (
              <Button
                variant="contained"
                onClick={handleGenerateLink}
                disabled={submitting || generatedLink}
                startIcon={<LinkIcon />}
              >
                {submitting ? "Generating..." : "Generate Link"}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleShareWithUser}
                disabled={submitting || !targetEmail.trim()}
                startIcon={<PersonAddIcon />}
              >
                {submitting ? "Sharing..." : "Share"}
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Layout>
  );
};

export default KycShare;